// API Test Automation Service
// Provides programmatic access to API test results

import { apiTestRunner, APITestSuite, APITestResult } from './apiTestRunner';

export interface APITestRunResult {
  sessionId: string;
  timestamp: Date;
  summary: {
    totalTests: number;
    passedTests: number;
    failedTests: number;
    skippedTests: number;
    successRate: number;
    totalDuration: number;
  };
  suites: APITestSuite[];
  statistics: any;
}

class APITestAutomation {
  private testRuns: APITestRunResult[] = [];

  async runAPITests(): Promise<APITestRunResult> {
    console.log('🚀 Starting API test automation...');
    
    const sessionId = `api-test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const startTime = new Date();
    
    try {
      // Run all API tests
      const suites = await apiTestRunner.runAllTests();
      
      // Calculate summary
      const allTests = suites.flatMap(suite => suite.tests);
      const totalTests = allTests.length;
      const passedTests = allTests.filter(t => t.status === 'pass').length;
      const failedTests = allTests.filter(t => t.status === 'fail').length;
      const skippedTests = allTests.filter(t => t.status === 'skip').length;
      const successRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;
      const totalDuration = suites.reduce((sum, suite) => sum + suite.duration, 0);
      
      const summary = {
        totalTests,
        passedTests,
        failedTests,
        skippedTests,
        successRate: Math.round(successRate * 100) / 100,
        totalDuration
      };
      
      const statistics = apiTestRunner.getStatistics();
      
      const result: APITestRunResult = {
        sessionId,
        timestamp: startTime,
        summary,
        suites,
        statistics
      };
      
      // Store the result
      this.testRuns.push(result);
      
      console.log('✅ API test automation complete:', result);
      return result;
      
    } catch (error) {
      console.error('❌ API test automation failed:', error);
      throw error;
    }
  }

  getLatestTestResults(): APITestRunResult | null {
    return this.testRuns.length > 0 ? this.testRuns[this.testRuns.length - 1] : null;
  }

  getAllTestRuns(): APITestRunResult[] {
    return [...this.testRuns];
  }

  getTestRun(sessionId: string): APITestRunResult | null {
    return this.testRuns.find(run => run.sessionId === sessionId) || null;
  }

  getTestStatistics(): any {
    if (this.testRuns.length === 0) {
      return {
        totalRuns: 0,
        averageSuccessRate: 0,
        totalTests: 0,
        averageDuration: 0
      };
    }

    const totalRuns = this.testRuns.length;
    const averageSuccessRate = this.testRuns.reduce((sum, run) => sum + run.summary.successRate, 0) / totalRuns;
    const totalTests = this.testRuns.reduce((sum, run) => sum + run.summary.totalTests, 0);
    const averageDuration = this.testRuns.reduce((sum, run) => sum + run.summary.totalDuration, 0) / totalRuns;

    return {
      totalRuns,
      averageSuccessRate: Math.round(averageSuccessRate * 100) / 100,
      totalTests,
      averageDuration: Math.round(averageDuration)
    };
  }

  clearTestRuns(): void {
    this.testRuns = [];
  }

  exportTestResults(sessionId?: string): string {
    const data = sessionId ? this.getTestRun(sessionId) : this.getAllTestRuns();
    return JSON.stringify(data, null, 2);
  }

  // Health check endpoint
  async healthCheck(): Promise<{ status: string; timestamp: Date; services: any }> {
    const services = {
      firebase: !!apiTestRunner,
      auth: true, // Will be tested in actual run
      firestore: true,
      analytics: true
    };

    return {
      status: 'healthy',
      timestamp: new Date(),
      services
    };
  }
}

export const apiTestAutomation = new APITestAutomation();
